import { Link } from 'react-router-dom';
import { fetchInsightsSalon } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { ADMIN_BASE } from '../../../utils/hosts';
import { fromIso, weekNumber } from '../../../core/format/dates';
import { occupancy, sum } from '../../../core/rules/insights/stats';
import { ChartFrame } from '../charts/ChartFrame';
import { BarChart } from '../charts/BarChart';
import { BarList } from '../charts/BarList';
import { Kpi } from '../Kpi';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt, ratio } from '../insightFormat';
import { weekdayLabel } from '../insightLabels';
import type { PeriodState } from '../../sales/usePeriodParams';

/** Salon: hoe vol de agenda is, wie niet komt, wat het meest geboekt wordt en de kuren. */
export function SalonTab({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsSalon, period.from, period.to);
  return <ReportView report={report} what="De saloncijfers" data-id="element-2147" data-mp="29dscu4fj3ga4">
      {s => {
      const total = occupancy(sum(s.byWeek.map(w => w.bookedMinutes)), sum(s.byWeek.map(w => w.openMinutes)));
      const held = s.appointments.total;
      return <>
            <section aria-label="Salon in deze periode" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-2148" data-mp="1nthtdzvqyd0l">
              <dl className="grid grid-cols-2 gap-6 lg:grid-cols-4" data-id="element-2149" data-mp="1sskytlfn2l93">
                <div className="flex flex-col" data-id="element-2150" data-mp="2ggvp5unpl2j4">
                  <dt className="text-[12px] text-muted" data-id="element-2151" data-mp="2gi6ysb3u54i5">Bezetting</dt>
                  <dd className="mt-1 font-display text-[1.9rem] font-light leading-none tabular-nums text-ink" data-id="element-2152" data-mp="2lf5o8gck0na0">{fmt(total, 'pct')}</dd>
                  <dd className="mt-1 text-[12px] text-muted" data-id="element-2153" data-mp="27is5z5074huh">Geboekt van de open tijd, zonder blokkades</dd>
                </div>
                <div className="flex flex-col" data-id="element-2154" data-mp="w1siqnspgt7b">
                  <dt className="text-[12px] text-muted" data-id="element-2155" data-mp="vzza52yj7ywo">Niet gekomen</dt>
                  <dd className="mt-1 text-[18px] tabular-nums text-ink" data-id="element-2156" data-mp="1046xavpcd6ss">{ratio(s.appointments.noShow, held)}</dd>
                  <dd className="mt-1 text-[12px] text-muted" data-id="element-2157" data-mp="32ohrec00a3yg">
                    {s.appointments.noShow} van {held} afspraken
                  </dd>
                </div>
                <div className="flex flex-col" data-id="element-2158" data-mp="2sz7wkods276n">
                  <dt className="text-[12px] text-muted" data-id="element-2159" data-mp="2sxwsengf6xf0">Geannuleerd</dt>
                  <dd className="mt-1 text-[18px] tabular-nums text-ink" data-id="element-2160" data-mp="2o0538175tpei">{ratio(s.appointments.cancelled, held + s.appointments.cancelled)}</dd>
                  <dd className="mt-1 text-[12px] text-muted" data-id="element-2161" data-mp="2f087jfmcrq9y">{s.appointments.cancelled} afspraken</dd>
                </div>
                <Kpi label="Omzet per behandeluur" value={s.revenuePerHourCents} kind="money" hint="Kuursessies tellen mee in de uren" data-id="element-2162" data-mp="2a0lvh9i9gg2q" />
              </dl>
            </section>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" data-id="element-2163" data-mp="89s8w29uuyl8">
              <ChartFrame title="Bezetting per week" fileName={`bezetting-per-week-${period.from}-${period.to}`} table={{
            columns: [{
              label: 'Week'
            }, {
              label: 'Geboekt (min)'
            }, {
              label: 'Open (min)'
            }, {
              label: 'Bezetting',
              kind: 'pct'
            }],
            rows: s.byWeek.map(w => [`Week ${weekNumber(fromIso(w.week))}`, w.bookedMinutes, w.openMinutes, occupancy(w.bookedMinutes, w.openMinutes)])
          }} data-id="element-2164" data-mp="87ehc3edw4if">
                <BarChart bars={s.byWeek.map(w => ({
              key: w.week,
              label: `wk ${weekNumber(fromIso(w.week))}`,
              values: [occupancy(w.bookedMinutes, w.openMinutes) ?? 0]
            }))} series={[{
              label: 'Bezetting',
              className: 'bg-clay/75'
            }]} format={n => fmt(n, 'pct')} max={100} data-id="element-2165" data-mp="pkxibc40cxh5" />
              </ChartFrame>

              <ChartFrame title="Per weekdag" description="Waar nog ruimte is" fileName={`bezetting-per-weekdag-${period.from}-${period.to}`} table={{
            columns: [{
              label: 'Dag'
            }, {
              label: 'Bezetting',
              kind: 'pct'
            }],
            rows: s.byWeekday.map(d => [weekdayLabel(d.weekday), occupancy(d.bookedMinutes, d.openMinutes)])
          }} data-id="element-2166" data-mp="6qilnt565gwh">
                <BarList max={100} rows={s.byWeekday.map(d => {
              const pct = occupancy(d.bookedMinutes, d.openMinutes);
              return {
                key: String(d.weekday),
                label: <span className="capitalize" data-id="element-2168" data-mp="3a202f3j0o3zf">{weekdayLabel(d.weekday)}</span>,
                value: pct ?? 0,
                display: fmt(pct, 'pct')
              };
            })} data-id="element-2167" data-mp="306g8gmo0jfdb" />
              </ChartFrame>
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" data-id="element-2169" data-mp="3iv3cr2yd80pu">
              <ChartFrame title="Meest geboekt" description="Afgeronde behandelingen, met de omzet uit de kassa (excl. btw)" tableOnly fileName={`behandelingen-${period.from}-${period.to}`} table={{
            columns: [{
              label: 'Behandeling'
            }, {
              label: 'Aantal'
            }, {
              label: 'Omzet',
              kind: 'money'
            }],
            rows: s.treatments.map(t => [t.name, t.count, t.revenueCents])
          }} data-id="element-2170" data-mp="3evkvch5r67rr" />

              <Panel title="Kuren" description="Vooruitbetaalde reeksen behandelingen" data-id="element-2171" data-mp="1co4bz2tskdp">
                <dl className="space-y-3 text-[14px]" data-id="element-2172" data-mp="6aze69t4euvx">
                  <div className="flex items-baseline justify-between gap-4" data-id="element-2173" data-mp="3oqjmgikmo79d">
                    <dt className="text-muted" data-id="element-2174" data-mp="3oof8f7t0lrvd">Verkocht</dt>
                    <dd className="tabular-nums text-ink" data-id="element-2175" data-mp="3thrwngts7wnl">{fmt(s.kuren.sold)}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4" data-id="element-2176" data-mp="3etnbm679b91n">
                    <dt className="text-muted" data-id="element-2177" data-mp="3js8dsb62k2sq">Helemaal afgemaakt</dt>
                    <dd className="text-right tabular-nums text-ink" data-id="element-2178" data-mp="35rnvdg42g8zr">
                      {ratio(s.kuren.completed, s.kuren.ended)}
                      <span className="block text-[12px] text-muted" data-id="element-2179" data-mp="39rpkyqwj1byo">van de kuren die afliepen</span>
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 border-t border-line pt-3" data-id="element-2180" data-mp="1gc3ap9hwqgtv">
                    <dt className="text-muted" data-id="element-2181" data-mp="16kguszumjtks">Nog te behandelen, al betaald</dt>
                    <dd className="tabular-nums text-ink" data-id="element-2182" data-mp="16ie7woorqgka">{fmt(s.kuren.prepaidOpenCents, 'money')}</dd>
                  </div>
                </dl>
              </Panel>
            </div>

            <p className="text-[12px] text-muted" data-id="element-2183" data-mp="1z8czoruckrsn">
              Productverkoop in de salon tegenover verbruik staat bij{' '}
              <Link to={`${ADMIN_BASE}/omzet?kanaal=salon&${period.query}`} className="text-ink underline underline-offset-2 hover:text-clay" data-id="element-2184" data-mp="1payj8s8fc30c">
                Omzet › Salon
              </Link>
              .
            </p>
          </>;
    }}
    </ReportView>;
}